import { useState, createContext, useContext, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import Home from './pages/Home';
import MyAccount from './pages/MyAccount';
import MyRepairs from './pages/MyRepairs';
//import MyDocuments from './pages/MyDocuments';
import UserInfo from './pages/UserInfo';
import Privacy from './pages/Privacy';
import Register from './pages/Register';
import ContactUs from './pages/ContactUs';
import AntiSocialBehaviour from './pages/AntiSocialBehaviour';
import ReportARepair from './pages/ReportARepair';
//import MyProfile from './pages/MyProfile';
import ComingSoon from './pages/ComingSoon';
import WeatherForecast from './pages/WeatherForecast';
import SignIn from './pages/SignIn';
import ProtectedRoute from './auth/ProtectedRoute';
import { initGA, logPageView, disableGA } from "./utils/analytics";

interface Account {
    accountReference: string;
    description: string;
    balance: number;
    subAccounts: any[];
    agreementDescription?: string;
    isFormerAccount: boolean;
    assetId: number;
    tenancyStartDate: string;
    tenancyEndDate: string;
    rentChargeEffectives: any;
}

interface SelectedAccountContextType {
    selectedAccount: Account | null;
    setSelectedAccount: (account: Account) => void;
}

export const SelectedAccountContext = createContext<SelectedAccountContextType | undefined>(undefined);

export const useSelectedAccount = () => {
    const context = useContext(SelectedAccountContext);
    if (!context) throw new Error('useSelectedAccount must be used within SelectedAccountContext.Provider');
    return context;
};

function App() {
    const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
    const location = useLocation();

    useEffect(() => {
        initGA();

        const handleAccept = () => {
            initGA();
            logPageView(location.pathname);
        };

        const handleDecline = () => {
            disableGA();
        };

        window.addEventListener("CookiebotOnAccept", handleAccept);
        window.addEventListener("CookiebotOnDecline", handleDecline);

        return () => {
            window.removeEventListener("CookiebotOnAccept", handleAccept);
            window.removeEventListener("CookiebotOnDecline", handleDecline);
        };
    }, []);

    useEffect(() => {
        logPageView(location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        // When page changes we need to re-trigger Reachdeck to translate
        const rd = (window as any).ReachDeck;

        if (rd) {
            // Grab user setting for Reachdeck stored in Local Storage
            const rdLocalStorageSettings: string | null = localStorage.getItem("API_SETTINGS_V2");

            if (rdLocalStorageSettings) {
                const rdSettings: any = JSON.parse(rdLocalStorageSettings);

                // Has the user changed the translated language from english to a.n.other language?
                if (rdSettings.translateSource !== rdSettings.translateDestination) {
                    // Yes, so toggle toolbar to force a page re-scan and translate
                    if (!rd.isToolbarActive()) {
                        rd.panel.toggleBar();
                    }
                    else {
                        rd.panel.toggleBar();

                        // N.B the delay is important here to give RD chance to keep up.
                        window.setTimeout(() => {
                            rd.panel.toggleBar();
                        }, 200);
                    }
                }
            }
        }
    }, [location]);

    return (
        <SelectedAccountContext.Provider value={{ selectedAccount, setSelectedAccount }}>
            <Layout>
                <Routes>
                    {/* Unprotected routes */}
                    <Route path="/signin" element={<SignIn />} />
                    <Route path="/privacy" element={<Privacy />} />

                    <Route path="/register" element={<Register />} />

                    {/* Protected routes */}
                    <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                    <Route path="/myaccount" element={<ProtectedRoute><MyAccount /></ProtectedRoute>} />
                    <Route path="/myrepairs" element={<ProtectedRoute><MyRepairs /></ProtectedRoute>} />
                    <Route
                        path="/mydocuments"
                        element={
                            <ProtectedRoute>
                                <ComingSoon
                                    title="MyDocuments will be available soon in your customer portal!"
                                    text={
                                        <>
                                            <p>This page will give you quick and easy access to important account documents all in one place.</p>
                                            <p>While we prepare this feature, you can still request copies or get the information you need by contacting us via our <a href="https://www.accentgroup.org/contact-us/" target="_blank" rel="noopener noreferrer">contact form</a> or calling <a href="tel:03456780555">0345 678 0555</a>.</p>
                                            <p>Thank you for your patience.</p>
                                        </>
                                    }
                                />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/userinfo" element={<ProtectedRoute><UserInfo /></ProtectedRoute>} />
                    <Route path="/contactus" element={<ProtectedRoute><ContactUs /></ProtectedRoute>} />
                    <Route path="/antisocialbehaviour" element={<ProtectedRoute><AntiSocialBehaviour /></ProtectedRoute>} />
                    <Route path="/reportarepair" element={<ProtectedRoute><ReportARepair /></ProtectedRoute>} />
                    <Route
                        path="/myprofile"
                        element={
                            <ProtectedRoute>
                                <ComingSoon
                                    title="MyProfile will be available soon in your customer portal!"
                                    text={
                                        <>
                                            <p>This page will let you view your account details and easily update your information whenever you need.</p>
                                            <p>While we're getting this ready, you can still make changes or check your details by contacting us via our <a href="https://www.accentgroup.org/contact-us/" target="_blank" rel="noopener noreferrer">contact form</a> or calling <a href="tel:03456780555">0345 678 0555</a>.</p>
                                            <p>Thank you for your patience.</p>
                                        </>
                                    }
                                />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/weatherforecast" element={<ProtectedRoute><WeatherForecast /></ProtectedRoute>} />
                </Routes>
            </Layout>
        </SelectedAccountContext.Provider>
    );
}

export default App;
